/**
 * CORE-700 — canonical gamification art, single source for every surface.
 *
 * Медальоны — Canonical v4 (11 штук, 256×256, тёмный эмалевый диск с золотым
 * ободом). Контракт (README canonical asset rules v4):
 *  - одни и те же PNG в светлой и тёмной темах, без перекраски и обрезки;
 *  - рекомендуемые UI-размеры 40/48/56/64/80 px;
 *  - hero-арт — НЕ медальон: только крупная иллюстрация внутри своей карточки
 *    (Пламя Любви / Свет Души), компактные места используют flame/soul-light;
 *  - good_deeds в актуальном продукте нет — иконки для него не существует.
 *
 * Фоновые полотна: path-background (public-profile delivery v1) — «Вехи пути»;
 * levels-background v3 (CORE-694 delivery) — spiritual-level card and explainer.
 */

export const MEDALLIONS = {
  flame: require('../../../../assets/gamification/flame.png'),
  sprout: require('../../../../assets/gamification/sprout.png'),
  growth: require('../../../../assets/gamification/growth.png'),
  soul_light: require('../../../../assets/gamification/soul-light.png'),
  shield_star: require('../../../../assets/gamification/shield-star.png'),
  progress: require('../../../../assets/gamification/progress.png'),
  calendar_plus: require('../../../../assets/gamification/calendar-plus.png'),
  live_discover: require('../../../../assets/gamification/live-discover.png'),
  live_start_portal: require('../../../../assets/gamification/live-start-portal.png'),
  event: require('../../../../assets/gamification/event.png'),
  master_media: require('../../../../assets/gamification/master-media.png'),
} as const;

export type MedallionKey = keyof typeof MEDALLIONS;

/**
 * CORE-663 — achievement-only presentation map.
 *
 * The files below are the untouched 256×256 Canonical v4 exports delivered with
 * the approved v10 achievements layout. Keep this map separate from MEDALLIONS:
 * the achievement catalogue is keyed by backend achievement ids, so the card
 * and drawer cannot accidentally drift to a similarly named generic asset.
 */
export const ACHIEVEMENT_MEDALLIONS = {
  first_spark: require('../../../../assets/gamification/achievements/flame.png'),
  week_of_light: require('../../../../assets/gamification/achievements/soul-light.png'),
  habit_sprout: require('../../../../assets/gamification/achievements/sprout.png'),
  // These two canonical v4 exports are byte-identical to the existing shared
  // files. Reuse them so Expo emits one physical asset instead of two copies.
  forty_days: require('../../../../assets/gamification/calendar-plus.png'),
  sacred_mala: require('../../../../assets/gamification/progress.png'),
  wheel_of_year: require('../../../../assets/gamification/achievements/shield-star.png'),
  first_practice: require('../../../../assets/gamification/achievements/sprout.png'),
  first_live: require('../../../../assets/gamification/achievements/live-discover.png'),
} as const;

export type AchievementMedallionKey = keyof typeof ACHIEVEMENT_MEDALLIONS;

/**
 * CORE-709 share cards are exported at 1200×630, so they must use the original
 * 256×256 Canonical v4 files instead of the compact 80/160/240 UI family.
 */
export const SHARE_MEDALLIONS = {
  flame: require('../../../../assets/gamification/achievements/flame.png'),
  sprout: require('../../../../assets/gamification/achievements/sprout.png'),
  growth: require('../../../../assets/gamification/level-badges/canonical-v4/growth.png'),
  soul_light: require('../../../../assets/gamification/achievements/soul-light.png'),
  shield_star: require('../../../../assets/gamification/achievements/shield-star.png'),
  progress: require('../../../../assets/gamification/progress.png'),
  calendar_plus: require('../../../../assets/gamification/calendar-plus.png'),
  live_discover: require('../../../../assets/gamification/achievements/live-discover.png'),
} as const;

/** Крупные иллюстрации карточек — по теме (light/dark), см. hero-art/ в Drive.
 *  *-masked — тот же canonical-арт с запечённым альфа-растворением краёв:
 *  эквивалент CSS-маски утверждённых макетов (в RN mask-image недоступен),
 *  чтобы иллюстрация вливалась в карточку без видимых границ. */
export const HERO_ART = {
  flame: {
    light: require('../../../../assets/gamification/love-flame-hero-light-masked.png'),
    dark: require('../../../../assets/gamification/love-flame-hero-dark-masked.png'),
  },
  soul: {
    light: require('../../../../assets/gamification/soul-light-hero-light-masked.png'),
    dark: require('../../../../assets/gamification/soul-light-hero-dark-masked.png'),
  },
} as const;

/** Декор карточек из фирменного полотна bg-register — с запечённым
 *  альфа-растворением, без резких краёв (как в утверждённых макетах). */
export const DECOR = {
  level: {
    light: require('../../../../assets/gamification/decor-level-light.png'),
    dark: require('../../../../assets/gamification/decor-level-dark.png'),
  },
  ring: {
    light: require('../../../../assets/gamification/decor-ring-light.png'),
    dark: require('../../../../assets/gamification/decor-ring-dark.png'),
  },
  glow: {
    light: require('../../../../assets/gamification/decor-glow-light.png'),
    dark: require('../../../../assets/gamification/decor-glow-dark.png'),
  },
} as const;

/** Фоновые полотна блоков — по теме. */
export const BACKDROPS = {
  /** Фирменное полотно bg-register (орбита над дюнами) — декоративные фоны
   *  «Уровня», «Достижений» (кольцо) и «Личных рекордов» (свечение). */
  brand: {
    light: require('../../../../assets/backgrounds/bg-register-light.webp'),
    dark: require('../../../../assets/backgrounds/bg-register-dark.webp'),
  },
  /** «Вехи пути» — гора с тропой (public-profile-path-background v1). */
  path: {
    light: require('../../../../assets/gamification/path-background-light.png'),
    dark: require('../../../../assets/gamification/path-background-dark.png'),
  },
  /** CORE-694 «Уровень духовного пути» — горы с орбитой (levels-background v3). */
  levels: {
    light: require('../../../../assets/gamification/levels-background-light.png'),
    dark: require('../../../../assets/gamification/levels-background-dark.png'),
  },
  /**
   * CORE-784 — тёплое полотно «Вех пути». По ТЗ оно живёт только в компактной
   * summary-зоне пояснения и не растягивается на всю шторку.
   */
  milestones: {
    light: require('../../../../assets/gamification/milestone-background-light.webp'),
    dark: require('../../../../assets/gamification/milestone-background-dark.webp'),
  },
  /** CORE-709 — final delivery v1 backdrop for all six share-card variants. */
  share: {
    light: require('../../../../assets/gamification/share-desert-light.png'),
    dark: require('../../../../assets/gamification/share-desert-dark.png'),
  },
  /**
   * CORE-727 — «Путь Света» card on another user's public profile (mobile
   * public-profile delivery v1). Unlike BACKDROPS.path, this art carries its
   * ridges across the whole canvas, so a narrow card shows painting instead of
   * the empty half of a right-weighted composition.
   *
   * The light theme keeps the path art until its counterpart is delivered.
   */
  publicProfilePath: {
    light: require('../../../../assets/gamification/path-background-light.png'),
    dark: require('../../../../assets/gamification/public-profile-path-background-dark.png'),
  },
  /**
   * CORE-749 — полотно для нативной шторки «Что такое Пламя Любви?».
   * Портретная композиция с декором по краям и пустым центром: текст
   * пояснений ложится в чистую середину, узор остаётся по периметру.
   * Только native — веб-версия модалки фон не использует.
   */
  flameInfo: {
    light: require('../../../../assets/gamification/flame-info-background-light.png'),
    dark: require('../../../../assets/gamification/flame-info-background-dark.webp'),
  },
} as const;
